import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, X } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ReviewsComponent = ({ config = {} }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const primaryColor = 'var(--theme-primary)';
  const title = config.title || 'What Our Clients Say';
  const subtitle = config.subtitle || "Don't just take our word for it - hear from our satisfied clients";
  const maxReviews = config.max_reviews || 10;
  const showCarousel = config.show_carousel !== false;
  const customReviewsUrl = config.reviews_url || ''; // Custom Google Maps URL for different business

  useEffect(() => {
    fetchReviews();
  }, [customReviewsUrl]); // Re-fetch when reviews_url changes

  const fetchReviews = async () => {
    try {
      setLoading(true);
      let reviewsToDisplay = [];

      // If custom reviews URL is provided, scrape from that URL
      if (customReviewsUrl) {
        const googleResponse = await fetch(`${API_BASE_URL}/api/reviews/scrape-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: customReviewsUrl })
        });
        if (googleResponse.ok) {
          const googleData = await googleResponse.json();
          if (!googleData.error && googleData.reviews) {
            reviewsToDisplay = googleData.reviews;
            // Filter to show only 4+ star reviews
            reviewsToDisplay = reviewsToDisplay.filter(review => review.rating >= 4);
          }
        }
      } else {
        // Check if dynamic reviews are enabled (default behavior)
        const settingsResponse = await fetch(`${API_BASE_URL}/api/reviews/check-dynamic`);
        const settingsData = await settingsResponse.json();

        if (settingsData.dynamic_reviews_enabled) {
          // Fetch Google reviews
          const googleResponse = await fetch(`${API_BASE_URL}/api/reviews/google`);
          if (googleResponse.ok) {
            const googleData = await googleResponse.json();
            if (!googleData.error) {
              reviewsToDisplay = googleData.reviews || [];
              // Filter to show only 4+ star reviews
              reviewsToDisplay = reviewsToDisplay.filter(review => review.rating >= 4);
            }
          }
        } else {
          // Fetch manual reviews
          const response = await fetch(`${API_BASE_URL}/api/reviews`);
          if (response.ok) {
            const data = await response.json();
            reviewsToDisplay = data.reviews || [];
          }
        }
      }

      // Limit to max reviews
      setTestimonials(reviewsToDisplay.slice(0, maxReviews));
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-advance carousel
  useEffect(() => {
    if (testimonials.length > 3 && showCarousel) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [testimonials.length, showCarousel]);

  const truncateText = (text, wordLimit) => {
    if (!text) return '';
    const words = text.split(' ');
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(' ') + '...';
  };

  const nextReview = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevReview = () => {
    setCurrentIndex((prev) => prev === 0 ? testimonials.length - 1 : prev - 1);
  };

  const openModal = (review) => {
    setSelectedReview(review);
    setShowModal(true);
  };

  if (loading) {
    return (
      <section className="bg-white py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <p className="text-gray-600 mt-2">{subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-gray-50 p-6 rounded-xl animate-pulse">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, j) => (
                    <div key={j} className="h-5 w-5 bg-gray-300 rounded mr-1"></div>
                  ))}
                </div>
                <div className="h-4 bg-gray-300 rounded mb-2"></div>
                <div className="h-4 bg-gray-300 rounded mb-4"></div>
                <div className="h-4 bg-gray-300 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return null; // Don't render if no reviews
  }

  return (
    <>
      <section className="bg-white py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center mb-4">
              <div className="h-px bg-gray-300 flex-grow max-w-24"></div>
              <div className="mx-4 px-6 py-2 bg-white backdrop-blur-sm rounded-full border border-gray-200 shadow-sm">
                <h2 className="font-bold text-gray-900 text-lg tracking-wide">{title}</h2>
              </div>
              <div className="h-px bg-gray-300 flex-grow max-w-24"></div>
            </div>
            <p className="text-gray-600" style={{ fontSize: '1rem' }}>{subtitle}</p>
          </div>

          {/* Reviews Carousel */}
          <div className="relative">
            {/* Navigation arrows */}
            {testimonials.length > 3 && showCarousel && (
              <>
                <button
                  onClick={prevReview}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
                  style={{ color: primaryColor }}
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={nextReview}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
                  style={{ color: primaryColor }}
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}

            {/* Reviews grid */}
            <div className="overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-500 ease-in-out">
                {(() => {
                  const displayReviews = [];
                  const count = Math.min(3, testimonials.length);
                  for (let i = 0; i < count; i++) {
                    const index = (currentIndex + i) % testimonials.length;
                    displayReviews.push(testimonials[index]);
                  }
                  return displayReviews;
                })().map((testimonial, idx) => (
                  <div
                    key={testimonial.id || idx}
                    className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow"
                    style={{
                      animation: 'fadeIn 0.5s ease-out',
                      animationDelay: `${idx * 0.1}s`,
                      animationFillMode: 'both'
                    }}
                  >
                    {/* Star rating */}
                    <div className="flex items-center mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-5 w-5 ${i < testimonial.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                        />
                      ))}
                      {testimonial.rating && (
                        <span className="ml-2 text-sm text-gray-600 font-medium">
                          {testimonial.rating}/5
                        </span>
                      )}
                    </div>

                    {/* Review text */}
                    <p className="text-gray-700 mb-4 italic">
                      "{truncateText(testimonial.text, 23)}"
                    </p>

                    {/* Read more button */}
                    {testimonial.text && testimonial.text.split(' ').length > 23 && (
                      <button
                        onClick={() => openModal(testimonial)}
                        className="text-sm font-medium mb-4 hover:underline"
                        style={{ color: primaryColor }}
                      >
                        Read More →
                      </button>
                    )}

                    {/* Author info */}
                    <div className="mt-auto">
                      <p className="font-semibold text-gray-900">{testimonial.owner_name}</p>
                      {testimonial.pet_name && (
                        <p className="text-sm text-gray-600">Pet parent to {testimonial.pet_name}</p>
                      )}
                      {testimonial.relative_time && (
                        <p className="text-xs text-gray-500 mt-1">{testimonial.relative_time}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination dots */}
            {testimonials.length > 1 && showCarousel && (
              <div className="flex justify-center mt-6 space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`h-2 rounded-full transition-all ${
                      currentIndex === index ? 'w-8' : 'w-2'
                    }`}
                    style={{
                      backgroundColor: currentIndex === index ? primaryColor : '#d1d5db'
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Review Modal */}
      {showModal && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex items-center mb-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-5 w-5 ${i < selectedReview.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                />
              ))}
            </div>

            <p className="text-gray-700 mb-4 italic">"{selectedReview.text}"</p>

            <div className="border-t pt-4">
              <p className="font-semibold text-gray-900">{selectedReview.owner_name}</p>
              {selectedReview.pet_name && (
                <p className="text-sm text-gray-600">Pet parent to {selectedReview.pet_name}</p>
              )}
              {selectedReview.relative_time && (
                <p className="text-xs text-gray-500 mt-1">{selectedReview.relative_time}</p>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default ReviewsComponent;
